import { useMediaQuery } from '@/hooks/useMediaQuery'
import GoogleLoginButton from '../common/GoogleLoginButton'
import { cn } from '@/lib/utils'

// Kalvium Logo SVG Component
const KalviumLogo = ({ className }: { className?: string }) => (
    <svg
        viewBox="0 0 155 32"
        fill="none"
        preserveAspectRatio="xMidYMid meet"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
    >
        <g>
            <path
                d="M0 0V31.9862H27.1314V0H0ZM3.5145 28.6184V17.709L23.6178 28.6184H3.5145ZM23.6178 24.6339L7.10795 15.9611L23.6178 7.39978V24.6331V24.6339ZM3.5145 14.2798V3.42659H23.6178L3.5145 14.2798Z"
                fill="#EF3837"
            />
            <path
                d="M49.8959 28.6189L43.1801 18.1965L40.0967 21.6127V28.6189H34.5801V3.80078H40.0967V14.8512L49.5535 3.80078H55.9607L46.7779 14.0494L56.3031 28.6198H49.8959V28.6189Z"
                fill="#EF3837"
            />
            <path
                d="M56.5078 23.5295C56.5078 20.7411 58.0841 18.4752 62.6063 18.0921L66.3754 17.7781C67.8488 17.6389 68.2595 17.1857 68.2595 16.4186C68.2595 14.8851 67.3343 14.0134 65.5531 14.0134C63.7719 14.0134 62.8467 14.8151 62.6746 16.4186H57.4667C57.4667 12.6884 59.797 10.2832 65.4848 10.2832C71.1726 10.2832 73.5029 12.6884 73.5029 16.4186V24.0173C73.5029 26.457 73.7086 28.0259 74.0511 28.6183H69.2885C68.8086 27.9913 68.5345 27.2934 68.5345 26.1085C67.541 27.9904 65.8964 28.9669 62.9496 28.9669C58.7352 28.9669 56.5078 26.841 56.5078 23.5286V23.5295ZM68.2604 21.3682V20.3571C68.0209 20.6356 67.3352 20.8103 66.3763 20.915L64.4576 21.0542C62.5043 21.2289 61.7512 22.0307 61.7512 23.1455C61.7512 24.505 62.5735 25.4114 64.4922 25.4114C66.8908 25.4114 68.2613 23.5641 68.2613 21.3682H68.2604Z"
                fill="#EF3837"
            />
            <path
                d="M76.1055 28.6189V3.80078H81.348V28.6189H76.1055Z"
                fill="#EF3837"
            />
            <path
                d="M88.9209 28.6175L82.582 10.6309H88.0303L92.1072 24.0164L96.185 10.6309H101.633L95.2944 28.6175H88.9218H88.9209Z"
                fill="#EF3837"
            />
            <path
                d="M102.523 5.68172C102.523 4.14832 103.585 2.99805 105.47 2.99805C107.355 2.99805 108.451 4.14832 108.451 5.68172C108.451 7.21513 107.389 8.3654 105.47 8.3654C103.552 8.3654 102.523 7.21513 102.523 5.68172ZM102.866 28.6179V10.6313H108.108V28.6179H102.866Z"
                fill="#EF3837"
            />
            <path
                d="M110.711 22.5176V10.6309H115.953V21.4719C115.953 23.5632 116.707 24.6789 118.489 24.6789C120.476 24.6789 121.778 23.2501 121.778 20.6702V10.6309H127.02V28.6175H122.052V25.8983C121.127 27.641 119.62 28.966 116.809 28.966C112.766 28.966 110.711 26.8748 110.711 22.5176Z"
                fill="#EF3837"
            />
            <path
                d="M149.703 28.6183V17.638C149.703 15.5113 148.983 14.5703 147.578 14.5703C146.002 14.5703 144.666 15.8599 144.666 18.4043V28.6175H139.664V17.6371C139.664 15.5104 138.91 14.5695 137.539 14.5695C135.929 14.5695 134.627 15.859 134.627 18.4034V28.6166H129.625V10.6317H134.593V13.3509C135.484 11.6082 136.854 10.2832 139.253 10.2832C141.788 10.2832 143.433 11.2942 144.221 13.4201C145.112 11.5727 146.791 10.2832 149.19 10.2832C152.856 10.2832 154.74 12.3744 154.74 16.7316V28.6183H149.703H149.703Z"
                fill="#EF3837"
            />
        </g>
    </svg>
)

const LogIn = () => {
    const isTablet = useMediaQuery('(max-width: 992px)')

    return (
        <div
            className={cn(
                'h-screen w-screen flex justify-center items-center bg-cover bg-center bg-no-repeat transition-colors bg-white dark:bg-gray-900 relative',
                isTablet
                    ? 'bg-[url(/images/login-bg-tablet.svg)] dark:bg-[url(/images/login-bg-tablet.svg)]'
                    : 'bg-[url(/images/login-bg.svg)] dark:bg-[url(/images/login-bg.svg)]'
            )}
        >
            {/* Overlay to dull the background image to 60-70% opacity */}
            <div className="absolute inset-0 bg-white opacity-35 dark:bg-gray-900 dark:opacity-30" />
            <div
                className={cn(
                    'relative z-10 border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-900 dark:text-white p-8 md:p-12 flex flex-col items-start gap-0 w-[328px] md:w-[596px] h-[380px] md:h-[364px] transition-colors overflow-visible'
                )}
            >
                <div className="overflow-visible">
                    <KalviumLogo className="w-[155px] h-8 max-w-none" />
                </div>
                <p className="mt-9 text-gray-900 dark:text-gray-100">
                    Unlock your potential and transform into a skilled software
                    professional with Kalvium&apos;s work-integrated computer
                    science program.
                </p>
                <div className="mt-8">
                    <GoogleLoginButton fontSize="sm" />
                </div>
                <button
                    className="text-gray-700 dark:text-gray-500 underline mt-6 font-bold cursor-pointer hover:text-[#EF3837] dark:hover:text-[#EF3837] transition-colors"
                    onClick={() => window.open('https://kalvium.com/')}
                >
                    Know More About Kalvium
                </button>
            </div>
        </div>
    )
}

export default LogIn
